import {
  createContext,
  use,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";

import { ar, type Catalog } from "./locales/ar";
import { en } from "./locales/en";

/**
 * A small, fully typed locale layer.
 *
 * We deliberately do not use a key-string i18n runtime here. The catalogue is
 * consumed as a typed object, so a missing or renamed key is a compile error
 * rather than a blank string discovered in production. Key parity between the
 * two locales is enforced by the `Catalog` type in `./locales/ar.ts`.
 *
 * `clients/web` uses i18next because it loads catalogues dynamically and needs
 * ICU plurals. The marketing site has neither requirement.
 */

export const LOCALES = ["ar", "en"] as const;
export type Locale = (typeof LOCALES)[number];

/** Arabic is the default language of the site. */
export const DEFAULT_LOCALE: Locale = "ar";

const CATALOGS: Record<Locale, Catalog> = { ar, en };

export const DIRECTION: Record<Locale, "rtl" | "ltr"> = {
  ar: "rtl",
  en: "ltr",
};

export function isLocale(value: string | undefined): value is Locale {
  return value === "ar" || value === "en";
}

/** The other locale — used by the language switcher. */
export function otherLocale(locale: Locale): Locale {
  return locale === "ar" ? "en" : "ar";
}

type LocaleContextValue = {
  locale: Locale;
  dir: "rtl" | "ltr";
  t: Catalog;
  /** Prefix a path with the active locale, e.g. "/features" -> "/ar/features". */
  path: (to: string) => string;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  locale,
  children,
}: {
  locale: Locale;
  children: ReactNode;
}) {
  const value = useMemo<LocaleContextValue>(() => {
    const dir = DIRECTION[locale];
    return {
      locale,
      dir,
      t: CATALOGS[locale],
      path: (to: string) => {
        const suffix = to.startsWith("/") ? to : `/${to}`;
        return suffix === "/" ? `/${locale}` : `/${locale}${suffix}`;
      },
    };
  }, [locale]);

  /**
   * Direction and language are applied to <html>, not a wrapper div, so that
   * logical CSS properties, scrollbars, text selection and the browser's own
   * UI all resolve in the correct direction.
   */
  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = DIRECTION[locale];
  }, [locale]);

  return <LocaleContext value={value}>{children}</LocaleContext>;
}

export function useLocale(): LocaleContextValue {
  const ctx = use(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used inside a LocaleProvider");
  }
  return ctx;
}

/**
 * Best-effort guess at the visitor's preferred locale. Arabic wins ties, since
 * Arabic is the default language of the product.
 */
export function detectLocale(): Locale {
  if (typeof navigator === "undefined") return DEFAULT_LOCALE;
  for (const lang of navigator.languages ?? [navigator.language]) {
    const tag = lang?.toLowerCase() ?? "";
    if (tag.startsWith("ar")) return "ar";
    if (tag.startsWith("en")) return "en";
  }
  return DEFAULT_LOCALE;
}
